/**
 * MemoryGuardian.js — V8 Heap Monitor & Size-Aware LRU Evictor
 * Next-Gen Memory Management for DICOM Viewers QWEN 3.8
 */

import { cornerstone } from '../core/init.js';
import { log } from '../utils/logger.js';
import { noteMemoryEvent } from '../ui/recoveryReport.js';

// [9.0.1] Fallback reclaimer для сброса целых томов, если кэш изображений неприкосновенен
let _reclaimer = null;
export function setVolumeReclaimer(fn) { _reclaimer = typeof fn === 'function' ? fn : null; }

// Helper для неблокирующего UI yield (Chrome 115+)
const yieldToMain = () => {
  if (typeof scheduler !== 'undefined' && 'yield' in scheduler) return scheduler.yield();
  return new Promise(resolve => setTimeout(resolve, 0));
};

export class MemoryGuardian extends EventTarget {
  // LRU Map: imageId -> { t: timestamp, size: bytes }
  #accessLog = new Map();
  #lockedImages = new Set();
  
  #monitorIntervalId = null;
  #isEvicting = false;
  // [10.0.2] Итог последнего прохода — чтобы «почему не освободилось» можно
  // было спросить из панели, а не выуживать из журнала.
  #lastEviction = { freedCount: 0, freedBytes: 0, staleCount: 0, volumeBound: 0, reclaimed: 0, critical: false, at: 0 };
  #lastNoteKey = '';
  #repeatCount = 0;
  #lastWarnKey = null;

  // Настройки и лимиты
  #MAX_TRACKED_IMAGES = 15000; // Защита от утечки строк
  #AVG_IMAGE_SIZE = 2 * 1024 * 1024; // Fallback 2MB, если точный размер неизвестен
  
  #WARNING_THRESHOLD = 0.75;
  #CRITICAL_THRESHOLD = 0.85;

  constructor(checkIntervalMs = 5000) {
    super();
    this.checkIntervalMs = checkIntervalMs;
    // typeof — модуль поднимается и вне окна (тесты, worker), где ни
    // performance.memory, ни navigator может не быть вовсе.
    this.hasMemoryAPI = !!(typeof performance !== 'undefined' && performance.memory);

    // Адаптация порогов под железо (navigator.deviceMemory)
    const deviceMem = (typeof navigator !== 'undefined' && navigator.deviceMemory) || 8; // 8GB fallback
    // [10.0.7] Телефон получает жёсткие пороги независимо от deviceMemory:
    // браузер не сообщает больше 8 ГБ, и по этому числу телефон с 12 ГБ
    // неотличим от настольной машины. А выгружают вкладку именно на телефоне.
    let mobileScreen = false;
    try { mobileScreen = !!(typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches); } catch (e) { /* */ }
    if (deviceMem <= 4 || mobileScreen) {
      this.#WARNING_THRESHOLD = 0.65;
      this.#CRITICAL_THRESHOLD = 0.75;
    }
  }

  /**
   * [10.0.7] Упреждающий сброс непоказанных томов — не дожидаясь критической
   * отметки. Раньше _reclaimer звался только когда память УЖЕ кончалась и
   * освободить кадры не вышло; смена серии при этом копила тома прежних
   * серий до самой беды. keepIds — кого не трогать (том текущей серии,
   * собираемый фоном: он нарочно ещё не показан).
   */
  async reclaim(keepIds = []) {
    if (!_reclaimer) return 0;
    try {
      const n = (await _reclaimer(keepIds)) || 0;
      if (n) log.info(`[MemoryGuardian] упреждающий сброс: томов ${n}`);
      return n;
    } catch (e) { log.debug('[MemoryGuardian] reclaim:', e.message); return 0; }
  }

  start() {
    if (this.#monitorIntervalId || !this.hasMemoryAPI) return;
    log.info('[MemoryGuardian] Activated. Monitoring V8 Heap...');
    this.#monitorIntervalId = setInterval(() => this.checkMemory(), this.checkIntervalMs);
  }

  stop() {
    if (this.#monitorIntervalId) {
      clearInterval(this.#monitorIntervalId);
      this.#monitorIntervalId = null;
    }
  }

  /**
   * Обновление LRU-статуса и размера кадра
   */
  touch(imageId, sizeInBytes = 0) {
    if (this.#accessLog.has(imageId)) {
      this.#accessLog.delete(imageId); // Удаляем, чтобы переместить в конец (LRU trick)
    }
    this.#accessLog.set(imageId, { t: performance.now(), size: sizeInBytes });

    // Hard-limit защита от бесконечного роста Map
    if (this.#accessLog.size > this.#MAX_TRACKED_IMAGES) {
      const oldestKey = this.#accessLog.keys().next().value;
      this.#accessLog.delete(oldestKey);
    }
  }

  /**
   * Принудительное удаление из трекера (вызывается при выгрузке из Cornerstone)
   */
  remove(imageId) {
    this.#accessLog.delete(imageId);
  }

  lock(imageIds) {
    const ids = Array.isArray(imageIds) ? imageIds : [imageIds];
    for (const id of ids) this.#lockedImages.add(id);
  }

  unlock(imageIds) {
    const ids = Array.isArray(imageIds) ? imageIds : [imageIds];
    for (const id of ids) this.#lockedImages.delete(id);
  }

  clear() {
    this.#accessLog.clear();
    this.#lockedImages.clear();
  }

  get accessLogSize() { return this.#accessLog.size; }
  get lockedSize() { return this.#lockedImages.size; }
  /** [10.0.2] Итог последнего вытеснения: сколько снято, сколько держит том. */
  get lastEviction() { return { ...this.#lastEviction }; }
  isMonitoring() { return !!this.#monitorIntervalId; }

  /**
   * Главный цикл проверки RAM
   */
  async checkMemory() {
    if (this.#isEvicting || !this.hasMemoryAPI) return;

    const memory = performance.memory;
    if (!memory.jsHeapSizeLimit) return; // Защита от деления на ноль

    const usageRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;

    if (usageRatio >= this.#WARNING_THRESHOLD) {
      this.dispatchEvent(new CustomEvent('memory-pressure', { detail: { ratio: usageRatio } }));
      
      const targetRatio = 0.60; 
      const bytesToFree = memory.usedJSHeapSize - (memory.jsHeapSizeLimit * targetRatio);
      
      await this.#evict(bytesToFree, usageRatio >= this.#CRITICAL_THRESHOLD);
    }
  }

  /**
   * Процесс вытеснения (Size-Aware Garbage Collection)
   */
  async #evict(bytesToFree, isCritical) {
    this.#isEvicting = true;
    log.warn(`[MemoryGuardian] Pressure high. Target free: ${(bytesToFree / 1048576).toFixed(2)} MB`);

    let freedBytes = 0;
    let freedCount = 0;
    let staleCount = 0;      // запись есть, кадра в кэше уже нет
    let volumeBound = 0;     // кадр жив, но принадлежит тому — снимать нельзя
    const startTime = performance.now();

    // [10.0.2] Рабочий API вытеснения и честный разбор трёх исходов.
    //
    // Что здесь было и почему это важно.
    //
    // 1) Присланная версия звала `cornerstone.imageCache?.removeImageLoadObject`
    //    с откатом на `cornerstone.imageLoader?.uncacheImage`. В Cornerstone 5.x
    //    НЕТ НИ ТОГО, НИ ДРУГОГО: `imageCache` не экспортируется, `uncacheImage`
    //    удалён. Обе ветки молча не выполнялись — страж проходил по журналу,
    //    вычёркивал записи, считал их освобождёнными и рапортовал «снял N кадров
    //    (X МБ)», не освободив ни байта. Рабочее имя — cornerstone.cache.
    //
    // 2) Но и с рабочим именем цикл был неправ. Настоящая сигнатура:
    //       removeImageLoadObject(imageId, { force = false })
    //    и она БРОСАЕТ в двух случаях (проверено по cache.js в node_modules):
    //       • кадра нет в кэше — 'imageId was not present in imageCache';
    //       • у кадра есть sharedCacheKey и не задан force —
    //         'Cannot decache an image with a shared cache key'.
    //    Второй случай — норма, а не сбой: ВСЕ кадры загруженного тома имеют
    //    общий ключ. То есть при открытом объёме бросок происходил на каждом
    //    кадре, `#accessLog.delete` (стоявший ПОСЛЕ вызова) не выполнялся
    //    никогда, и журнал рос до потолка в 15 000 записей, а вытеснение
    //    работало вхолостую — при этом отчёт показывал ровный ноль без причины.
    //    Это и есть та «утечка», от которой телефон уходил в перезагрузку
    //    вкладки: чинить память было уже нечем.
    //
    // Теперь три исхода разведены:
    //   • кадра в кэше нет → запись мертва, вычёркиваем (памяти не прибавилось);
    //   • кадр принадлежит тому → оставляем в журнале и считаем отдельно,
    //     чтобы отчёт сказал «снимать нечего, N кадров держит том», а не «0»;
    //   • кадр снят → считаем байты.
    //
    // force не включаем: с ним 3D чернел под нагрузкой — вывод прежней версии,
    // не догадка. Тома сбрасывает _reclaimer целиком, это ниже.
    const cache = cornerstone.cache;
    if (!cache?.removeImageLoadObject) {
      // Кэша нет — вычёркивать из журнала нечего, иначе потеряем след
      log.warn('[MemoryGuardian] cornerstone.cache.removeImageLoadObject недоступен — вытеснение кадров пропущено');
    } else {
      // Итерируемся напрямую по Map без создания массива!
      // В ES6+ удаление из Map во время for...of безопасно.
      for (const [imageId, data] of this.#accessLog) {
        if (this.#lockedImages.has(imageId)) continue;

        // В обычном режиме не трогаем то, что смотрели последние 10 сек
        if (!isCritical && (startTime - data.t) < 10000) continue;

        // Спрашиваем до удаления: getImageLoadObject не бросает, а
        // removeImageLoadObject на отсутствующем кадре — бросает.
        let present = true;
        try { present = !!cache.getImageLoadObject?.(imageId); } catch (e) { present = false; }
        if (!present) {
          this.#accessLog.delete(imageId);
          staleCount++;
          continue;
        }

        try {
          cache.removeImageLoadObject(imageId);
        } catch (e) {
          // Кадр держит том. Запись НЕ трогаем: том закроют — кадр станет
          // обычным, и тогда его можно будет снять.
          volumeBound++;
          continue;
        }

        this.#accessLog.delete(imageId);

        const imgSize = data.size || this.#AVG_IMAGE_SIZE;
        freedBytes += imgSize;
        freedCount++;

        // Неблокирующий Yield каждые 50 картинок
        if (freedCount % 50 === 0) await yieldToMain();

        // Прерываем цикл, когда ОСВОБОДИЛИ ДОСТАТОЧНО БАЙТ
        if (freedBytes >= bytesToFree || freedCount > 1000) break;
      }
    }

    // [9.0.1] КРАЙНЯЯ МЕРА: Сброс непоказанных томов
    let reclaimed = 0;
    if (freedCount === 0 && isCritical && _reclaimer) {
      try { reclaimed = (await _reclaimer()) || 0; } catch (e) { log.debug('[MemoryGuardian] reclaim:', e.message); }
    }

    this.#lastEviction = {
      freedCount, freedBytes, staleCount, volumeBound, reclaimed,
      critical: !!isCritical, at: Date.now(),
    };

    this.#generateReport(freedCount, freedBytes, reclaimed, isCritical, { staleCount, volumeBound });

    // Только для Dev-окружения с флагом --expose-gc. typeof — потому что вне
    // браузера (тесты, worker) window не объявлен вовсе, и обращение к нему
    // роняло бы весь проход ReferenceError'ом уже после полезной работы.
    if (typeof window !== 'undefined' && window.gc) window.gc();

    this.#isEvicting = false;
    this.dispatchEvent(new CustomEvent('memory-cleared', {
      detail: { freedCount, freedBytes, staleCount, volumeBound, reclaimed },
    }));
  }

  #generateReport(freedCount, freedBytes, reclaimed, isCritical, extra = {}) {
    try {
      // [10.0.2] Причина нуля важнее самого нуля. «Снял 0 кадров» одинаково
      // выглядит и когда журнал пуст, и когда все кадры держит открытый том —
      // а действия при этом разные: во втором случае помогает только сброс
      // тома, и знать это надо до того, как вкладку убьёт браузер.
      const why = [];
      if (extra.volumeBound) why.push(`держит том: ${extra.volumeBound}`);
      if (extra.staleCount) why.push(`уже не в кэше: ${extra.staleCount}`);
      const detail = `${isCritical ? 'критический режим' : 'обычный'}, под защитой: ${this.#lockedImages.size}`
        + (why.length ? `, ${why.join(', ')}` : '');
      const what = reclaimed
        ? `снял ${freedCount} кадров (${(freedBytes / 1048576).toFixed(1)}MB), сбросил ${reclaimed} томов`
        : `снял ${freedCount} кадров (${(freedBytes / 1048576).toFixed(1)}MB)`;
      
      const key = `${what}|${detail}`;
      if (key !== this.#lastNoteKey || reclaimed) {
        this.#lastNoteKey = key;
        this.#repeatCount = 0;
        noteMemoryEvent('Страж памяти', what, detail);
      } else if (++this.#repeatCount % 20 === 0) {
        noteMemoryEvent('Страж памяти', what, `${detail} · повторилось ${this.#repeatCount} раз`);
      }
      
      if (freedCount === 0 && !reclaimed && isCritical && this.#lockedImages.size) {
        if (this.#lastWarnKey !== 'paralysis') {
          this.#lastWarnKey = 'paralysis';
          noteMemoryEvent('Страж памяти', 'освободить НЕЧЕГО', `весь кэш под защитой (${this.#lockedImages.size} кадров)`);
        }
      } else { this.#lastWarnKey = null; }
    } catch (e) { /* Игнорируем ошибки UI репортера */ }
  }
}

export const memoryGuardian = new MemoryGuardian();

// ── Enterprise API (Исправлено) ──
export function getMemoryGuardianState() {
  const mg = memoryGuardian;
  const mem = mg.hasMemoryAPI ? performance.memory : null;
  return {
    monitoring: mg.isMonitoring(), // Используется публичный метод
    hasMemoryAPI: mg.hasMemoryAPI,
    trackedImages: mg.accessLogSize,
    lockedImages: mg.lockedSize,
    lastEviction: mg.lastEviction,   // [10.0.2] почему в прошлый раз не освободилось
    heapUsedMB: mem ? +(mem.usedJSHeapSize / 1048576).toFixed(0) : null,
    heapTotalMB: mem ? +(mem.totalJSHeapSize / 1048576).toFixed(0) : null,
    heapLimitMB: mem ? +(mem.jsHeapSizeLimit / 1048576).toFixed(0) : null,
    usagePercent: mem && mem.jsHeapSizeLimit ? +(mem.usedJSHeapSize / mem.jsHeapSizeLimit * 100).toFixed(1) : null,
  };
}
export function forceMemoryCheck() { memoryGuardian.checkMemory(); }